import React, { useState } from 'react';
import { contactContent } from '@/content/contact';
import { contactInquirySchema } from '@/lib/contactValidation';
import { apiPost } from '@/lib/apiClient';
import { Button, Icon, Reveal } from '@/components/ui';

const { form } = contactContent;

const ContactForm: React.FC = () => {
    const [formData, setFormData] = useState({
        Full_Name: '',
        _replyto: '',
        Phone_Number: '',
        _subject: 'General Inquiry',
        Message: '',
        // Anti-spam honeypot: hidden from humans, often auto-filled by bots.
        // Must stay empty; the server drops any submission that fills it.
        Company_Website: ''
    });
    const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error when user starts typing
        if (fieldErrors[name]) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const result = contactInquirySchema.safeParse(formData);
        if (result.success) {
            setFieldErrors({});
            return true;
        }

        const errors: Record<string, string> = {};
        result.error.issues.forEach((issue) => {
            const path = issue.path[0] as string;
            if (!errors[path]) {
                errors[path] = issue.message;
            }
        });
        setFieldErrors(errors);
        return false;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) {
            setStatus('error');
            return;
        }

        setStatus('sending');
        setFieldErrors({});

        try {
            await apiPost('/api/send-inquiry', formData);

            setStatus('success');
            setFormData({
                Full_Name: '',
                _replyto: '',
                Phone_Number: '',
                _subject: 'General Inquiry',
                Message: '',
                Company_Website: ''
            });
            setTimeout(() => setStatus('idle'), 4000);
        } catch {
            setStatus('error');
        }
    };

    return (
        <>
            <Reveal as="div" className="contact-form-box card">
                <form id="contact-form" onSubmit={handleSubmit}>
                    {/* Honeypot field: visually hidden and removed from tab order.
                        Real users never see or fill it; bots frequently do. */}
                    <input
                        type="text"
                        name="Company_Website"
                        value={formData.Company_Website}
                        onChange={handleChange}
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        style={{
                            position: 'absolute',
                            left: '-9999px',
                            width: '1px',
                            height: '1px',
                            opacity: 0,
                            overflow: 'hidden'
                        }}
                    />

                    {form.fields.map((field) => (
                        <React.Fragment key={field.name}>
                            <input
                                type={field.type}
                                name={field.name}
                                value={formData[field.name as keyof typeof formData]}
                                onChange={handleChange}
                                placeholder={field.placeholder}
                                aria-label={field.ariaLabel}
                                className={fieldErrors[field.name] ? 'error' : ''}
                            />
                            {fieldErrors[field.name] && (
                                <div className="field-error" aria-live="polite">
                                    {fieldErrors[field.name]}
                                </div>
                            )}
                        </React.Fragment>
                    ))}

                    <select 
                        name={form.subject.name} 
                        value={formData._subject}
                        onChange={handleChange}
                        aria-label={form.subject.ariaLabel} 
                        className={fieldErrors[form.subject.name] ? 'error' : ''}
                    >
                        {form.subject.options.map((option) => (
                            <option key={option.value} value={option.value} disabled={option.disabled}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    {fieldErrors[form.subject.name] && (
                        <div className="field-error" aria-live="polite">
                            {fieldErrors[form.subject.name]}
                        </div>
                    )}

                    <textarea 
                        name={form.message.name} 
                        value={formData.Message}
                        onChange={handleChange}
                        placeholder={form.message.placeholder} 
                        aria-label={form.message.ariaLabel} 
                        rows={form.message.rows} 
                        className={fieldErrors[form.message.name] ? 'error' : ''}
                    ></textarea>
                    {fieldErrors[form.message.name] && (
                        <div className="field-error" aria-live="polite">
                            {fieldErrors[form.message.name]}
                        </div>
                    )}

                    <Button
                        type="submit"
                        className="btn-primary"
                        disabled={status === 'sending'}
                    >
                        {status === 'sending' ? form.submit.sending : form.submit.idle}
                    </Button>

                    <div className="form-status-container" aria-live="assertive">
                        {status === 'error' && Object.keys(fieldErrors).length === 0 && (
                            <div className="form-status error">
                                {form.errors.submit}
                            </div>
                        )}
                        {status === 'success' && (
                            <div className="form-status success">
                                {form.success.message}
                            </div>
                        )}
                    </div>
                </form>
            </Reveal>

            <div id="success-modal" className={status === 'success' ? 'modal-visible' : 'modal-hidden'}>
                <div className="modal-content">
                    <Icon name="check-circle" className="modal-icon" />
                    <h2>{form.success.title}</h2>
                    <p>{form.success.message}</p>
                </div>
            </div>
        </>
    );
};

export default ContactForm;
